import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import AddClientsModal from "./addClientsModal";
import { getAllClients, setClient } from "../../../store/slices/clientsSlice";
import { useDebounce } from "../../../Hooks/useDebounce";

const ManageClientsTable = () => {
  const dispatch = useDispatch();
  const client = useSelector((state) => state.clients?.selectedClient?.value);
  const [data, setData] = useState([]);
  const [totalCount, setCount] = useState(0);
  const [editItem, setEditItem] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const columnFilterSearch = useDebounce(columnFilters, 300);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const fetchData = async (payload) => {
    const sortField = sorting.length > 0 ? sorting[0].id : undefined;
    const sortOrder =
      sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : undefined;

    try {
      const response = await dispatch(
        getAllClients({
          pagination: true,
          limit: pageSize,
          offset: page * pageSize,
          sortBy: sortField,
          sortOrder: sortOrder,
          ...payload,
        }),
      );
      if (response?.payload) {
        const clients =
          response.payload?.clients ??
          (Array.isArray(response.payload) ? response.payload : []);
        setData(clients);
        setCount(response.payload?.count ?? clients.length);
      }
    } catch (error) {
      console.error("Error fetching interactions:", error);
    }
  };

  const getClients = () => {
    fetchData({ searchQuery: debouncedSearch, filters: columnFilterSearch });
  };

  const handleEdit = (clientData) => {
    setEditItem(clientData);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditItem({});
    setModalOpen(true);
  };

  const handlePageChange = (event) => {
    const newState =
      typeof event === "function"
        ? event({ pageIndex: page, pageSize: pageSize })
        : event;
    setPage(newState.pageIndex);
    setPageSize(newState.pageSize);
  };

  const handleOnChangeSearch = (value) => {
    setSearchQuery(value);
    setPage(0);
  };

  const columns = useMemo(
    () => [
      { accessorKey: "ID", header: "ID", size: 150 },
      {
        accessorKey: "client_name",
        header: "Client Name",
        size: 150,
        Cell: ({ row, renderedCellValue }) => (
          <span
            className="text-primary fw-bold"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row.original);
            }}
          >
            {renderedCellValue}
          </span>
        ),
      },
      { accessorKey: "client_website", header: "Registered Link", size: 150 },
      { accessorKey: "created_at", header: "Added On", size: 150 },
      { accessorKey: "client_status", header: "Status", size: 150 },
      { accessorKey: "tenant_slug", header: "Tenant", size: 150 },
    ],
    [],
  );

  useEffect(() => {
    fetchData({ searchQuery: debouncedSearch, filters: columnFilterSearch });
  }, [dispatch, page, pageSize, debouncedSearch, columnFilterSearch, sorting]);

  const table = useMaterialReactTable({
    columns,
    data,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => rowAction(event, row),
      sx: { cursor: "pointer" },
    }),
    initialState: { density: "compact" },
    globalFilter: searchQuery,
    onGlobalFilterChange: handleOnChangeSearch,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: totalCount || 0,
    onPaginationChange: handlePageChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      pagination: { pageIndex: page, pageSize: pageSize },
      sorting,
      columnFilters,
      globalFilter: searchQuery,
    },
    renderTopToolbarCustomActions: () => (
      <div className="d-flex">
        <button
          className="btn bg-primary"
          disabled={client === "ts"}
          onClick={handleAdd}
        >
          Add Client
        </button>
      </div>
    ),
  });

  const rowAction = (event, row) => {
    dispatch(setClient(row.original));
  };

  const toggleCreateModal = () => {
    getClients();
    setModalOpen(!isModalOpen);
    if (isModalOpen) {
      setEditItem({}); // Clear data when closing
    }
  };

  return (
    <Fragment>
      <AddClientsModal
        isOpen={isModalOpen}
        toggle={toggleCreateModal}
        editItem={editItem}
      />
      <Breadcrumbs
        mainTitle="Manage Clients"
        parent="Clients"
        parentPath="manage-clients"
        title="Clients List"
      />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <MaterialReactTable table={table} />
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ManageClientsTable;
